/**
 * assume DOM:
 *
 * ticker container
 *   ticker list
 *     dial container
 *       dial list
 *         digit 0
 *         ...
 *         digit 9
 *         digit 0
 *     ...
 *
 */

export const ticker = document.querySelector('.ticker');

const dials = Array.from(ticker.querySelectorAll('.dial'));

let queue = Promise.resolve();
let lastQueued = null;

let disabled = true;

/**
 * Disable queueing
 */
export function disable() {
  disabled = true;
}

/**
 * Enable queueing
 */
export function enable() {
  disabled = false;
}

/**
 * Change a dial from nine to zero (special case)
 * @param {DOMNode} dial dial to change
 * @return {Promise} resolves when complete
 */
function dialNineToZero(dial) {
  return new Promise(resolve => {
    dial.dataset.digit = '10';
    dial.addEventListener('transitionend', () => {
      dial.classList.add('no-transition');
      dial.dataset.digit = '0';
      setTimeout(() => {
        dial.classList.remove('no-transition');
        resolve(0);
      }, 0);
    }, {once: true});
  });
}

/**
 * Change a dial from zero to nine (special case)
 * @param {DOMNode} dial dial to change
 * @return {Promise} resolves when complete
 */
function dialZeroToNine(dial) {
  return new Promise(resolve => {
    dial.classList.add('no-transition');
    dial.dataset.digit = '10';
    setTimeout(() => {
      dial.classList.remove('no-transition');
      dial.dataset.digit = '9';
      dial.addEventListener('transitionend', () => resolve(9), {once: true});
    }, 0);
  });
}

/**
 * Increment a dial by one
 * @param {DOMNode} dial dial to increment
 * @return {Promise} resolves when complete
 */
function incrementDial(dial) {
  if (dial.dataset.digit === '9') {
    return dialNineToZero(dial);
  } else {
    return new Promise(resolve => {
      ++dial.dataset.digit;
      dial.addEventListener('transitionend', () => resolve(+dial.dataset.digit), {once: true});
    });
  }
}

/**
 * Decrement a dial by one
 * @param {DOMNode} dial dial to decrement
 * @return {Promise} resolves when complete
 */
function decrementDial(dial) {
  if (dial.dataset.digit === '0') {
    return dialZeroToNine(dial);
  } else {
    return new Promise(resolve => {
      --dial.dataset.digit;
      dial.addEventListener('transitionend', () => resolve(+dial.dataset.digit), {once: true});
    });
  }
}

/**
 * Change a dial to a new digit in a specific direction (forwards = 1 / backwards = -1)
 * @param {DOMNode} dial dial to change
 * @param {Number} newDigit new dial digit
 * @param {Number} direction direction to change dial (forwards = 1 / backwards = -1)
 * @return {Promise} resolves when complete with the new digit
 */
async function changeDial(dial, newDigit, direction = 1) {
  while (+dial.dataset.digit !== newDigit) {
    if (direction > 0) {
      await incrementDial(dial);
    } else {
      await decrementDial(dial);
    }
  }
  return newDigit;
}

/**
 * Synchronously/instantaneously set the ticker to a value
 * @param {Number} value the value to set
 */
export function setTicker(value) {
  ticker.dataset.digits = value;
  const digits = String(value).padStart(dials.length, '0');
  dials.forEach((dial, d) => dial.dataset.digit = digits[d]);
}

/**
 * Change the ticker to a new value
 * @param {Number} newValue new value for ticker
 * @return {Promise} resolves when ticker change completes
 */
export async function changeTicker(newValue) {
  let value = +ticker.dataset.digits;

  ticker.dataset.digits = value;

  const increment = (newValue >= value) ? +1 : -1;

  while (value !== newValue) {
    value += increment;
    const digits = String(value).padStart(dials.length, '0');
    await Promise.all(dials.map((dial, d) => changeDial(dial, +digits[d], increment)));
    ticker.dataset.digits = digits;
  }

  return newValue;
}

/**
 * Queue a change to the ticker which will run when all queued changes are complete
 * @param {Number} value the new value
 */
export function queueTicker(value) {
  if (disabled) {
    console.log('ticker disabled');
    return;
  }

  // don't repeatedly queue the same value
  if (value === lastQueued) {
    return;
  }

  console.log(`queueing ${value}`);

  // add new value to the end of the queue
  queue = queue.then(() => changeTicker(value));

  lastQueued = value;
}

/**
 * Call a function when the current year change completes, or immediately if none is in progress
 * @param {Function} fn function
 */
export async function onTickerComplete(fn) {
  await queue;
  fn();
}

// set the ticker to a sane starting value so it does not try to animate from 0000 - 1996
setTicker(1996);
